/**
 * Tax & Compliance Overview Dashboard
 * Aggregates deductible expenses, revenue, estimated taxes across the app.
 * Uses progressive federal/state bracket engine for accurate tax projections.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer,
} from 'recharts';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DollarSign,
  TrendingDown,
  Receipt,
  Calculator,
  FileText,
  ArrowRight,
  Landmark,
  PiggyBank,
  BarChart3,
  Percent,
  ShieldCheck,
} from 'lucide-react';

// ============================================
// Types
// ============================================

interface TaxSummary {
  totalRevenue: number;
  totalExpenses: number;
  totalDeductibleExpenses: number;
  totalDeductibleSubscriptions: number;
  totalDeductions: number;
  totalTaxCollected: number;
  totalEstimatedPaid: number;
  netProfit: number;
}

interface BracketResult {
  bracket: string;
  rate: number;
  taxableInThisBracket: number;
  taxForThisBracket: number;
}

interface TaxProjection {
  grossRevenue: number;
  totalDeductions: number;
  taxableIncome: number;
  federalTax: number;
  federalEffectiveRate: number;
  federalMarginalRate: number;
  federalBracketBreakdown: BracketResult[];
  selfEmploymentTax: number;
  selfEmploymentEffectiveRate: number;
  selfEmploymentTaxableBase: number;
  stateTax: number;
  stateEffectiveRate: number;
  stateBracketBreakdown: BracketResult[];
  totalTax: number;
  totalEffectiveRate: number;
  netIncomeAfterTax: number;
  quarterlyEstimate: number;
  monthlySetAside: number;
}

interface CategoryBreakdown {
  taxCategory: string;
  itemCount: number;
  totalAmount: number;
}

interface QuarterlyRevenue {
  quarter: number;
  revenue: number;
  taxCollected: number;
}

interface BusinessProfile {
  id: string;
  name: string;
}

interface TaxSummaryResponse {
  summary: TaxSummary;
  taxProjection: TaxProjection | null;
  deductionsByCategory: CategoryBreakdown[];
  quarterlyRevenue: QuarterlyRevenue[];
}

// ============================================
// Constants
// ============================================

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

const PIE_COLORS = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981'];

const formatPct = (rate: number) => `${(rate * 100).toFixed(1)}%`;
const formatPctShort = (rate: number) => `${(rate * 100).toFixed(0)}%`;

// ============================================
// Component
// ============================================

export function TaxOverview() {
  const navigate = useNavigate();
  const [taxYear, setTaxYear] = useState(currentYear);
  const [businessProfileId, setBusinessProfileId] = useState<string>('all');

  const { data: profilesData } = useQuery({
    queryKey: ['business-profiles'],
    queryFn: () => api.getBusinessProfiles(),
  });
  const profiles = (profilesData?.data || []) as BusinessProfile[];

  const { data, isLoading } = useQuery({
    queryKey: ['tax-summary', taxYear, businessProfileId],
    queryFn: async () => {
      const result = await api.getTaxSummary({
        taxYear,
        businessProfileId: businessProfileId !== 'all' ? businessProfileId : undefined,
      });
      return result.data as TaxSummaryResponse;
    },
  });

  const summary = data?.summary;
  const taxProjection = data?.taxProjection;
  const deductionsByCategory = data?.deductionsByCategory || [];
  const quarterlyRevenue = data?.quarterlyRevenue || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Tax amounts from projection (progressive brackets) or fallback
  const totalTaxOwed = taxProjection?.totalTax || 0;
  const effectiveRate = taxProjection?.totalEffectiveRate || 0;
  const estimatedPaid = summary?.totalEstimatedPaid || 0;
  const remainingLiability = Math.max(0, totalTaxOwed - estimatedPaid);

  // Data for tax breakdown pie chart
  const taxBreakdownData = taxProjection ? [
    { name: 'Federal', value: taxProjection.federalTax },
    { name: 'Self-Employment', value: taxProjection.selfEmploymentTax },
    ...(taxProjection.stateTax > 0 ? [{ name: 'State', value: taxProjection.stateTax }] : []),
  ].filter(d => d.value > 0) : [];

  // Data for quarterly revenue bar chart
  const quarterlyChartData = [1, 2, 3, 4].map(q => {
    const qData = quarterlyRevenue.find(qr => qr.quarter === q);
    return {
      name: `Q${q}`,
      revenue: qData?.revenue || 0,
      taxCollected: qData?.taxCollected || 0,
    };
  });

  // Data for income waterfall
  const waterfallData = taxProjection ? [
    { name: 'Revenue', amount: taxProjection.grossRevenue, fill: '#3b82f6' },
    { name: 'Deductions', amount: -taxProjection.totalDeductions, fill: '#10b981' },
    { name: 'Federal Tax', amount: -taxProjection.federalTax, fill: '#ef4444' },
    { name: 'SE Tax', amount: -taxProjection.selfEmploymentTax, fill: '#f97316' },
    ...(taxProjection.stateTax > 0 ? [{ name: 'State Tax', amount: -taxProjection.stateTax, fill: '#8b5cf6' }] : []),
    { name: 'Net Income', amount: taxProjection.netIncomeAfterTax, fill: '#059669' },
  ] : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tax & Compliance Dashboard</h1>
          <p className="text-muted-foreground">Tax year overview with progressive bracket calculations</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={businessProfileId} onValueChange={setBusinessProfileId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Businesses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Businesses</SelectItem>
              {profiles.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={taxYear.toString()} onValueChange={(v) => setTaxYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map(y => (
                <SelectItem key={y} value={y.toString()}>TY {y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards — Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gross Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(summary?.totalRevenue || 0)}</div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(summary?.totalTaxCollected || 0)} tax collected
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Deductions</CardTitle>
            <TrendingDown className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">
              {formatCurrency(summary?.totalDeductions || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {deductionsByCategory.length} IRS categories
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxable Income</CardTitle>
            <Calculator className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(taxProjection?.taxableIncome || summary?.netProfit || 0)}</div>
            <p className="text-xs text-muted-foreground">
              After deductions & SE adjustment
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Effective Tax Rate</CardTitle>
            <Percent className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">
              {formatPct(effectiveRate)}
            </div>
            <p className="text-xs text-muted-foreground">
              {taxProjection ? `Marginal: ${formatPctShort(taxProjection.federalMarginalRate)}` : 'Progressive brackets'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Summary Cards — Row 2: Tax Liability */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tax Liability</CardTitle>
            <Landmark className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{formatCurrency(totalTaxOwed)}</div>
            <p className="text-xs text-muted-foreground">
              Federal + SE + State
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Estimated Paid</CardTitle>
            <PiggyBank className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{formatCurrency(estimatedPaid)}</div>
            <p className="text-xs text-muted-foreground">
              {totalTaxOwed > 0 ? `${formatPctShort(estimatedPaid / totalTaxOwed)} of liability` : 'No liability'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Remaining Due</CardTitle>
            <ShieldCheck className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${remainingLiability > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
              {formatCurrency(remainingLiability)}
            </div>
            <p className="text-xs text-muted-foreground">
              {taxProjection ? `${formatCurrency(taxProjection.monthlySetAside)}/mo recommended` : '—'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Net After Tax</CardTitle>
            <DollarSign className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">
              {formatCurrency(taxProjection?.netIncomeAfterTax || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Take-home income
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tax Breakdown Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Tax Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {taxBreakdownData.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Calculator className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No tax data available</p>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={200}>
                  <PieChart>
                    <Pie
                      data={taxBreakdownData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {taxBreakdownData.map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-3">
                  {taxBreakdownData.map((entry, index) => (
                    <div key={entry.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                        />
                        <span className="text-sm">{entry.name}</span>
                      </div>
                      <span className="text-sm font-medium">{formatCurrency(entry.value)}</span>
                    </div>
                  ))}
                  <div className="border-t pt-2 flex items-center justify-between font-medium">
                    <span className="text-sm">Total</span>
                    <span className="text-sm">{formatCurrency(totalTaxOwed)}</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quarterly Revenue Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Quarterly Revenue & Tax Collected</CardTitle>
          </CardHeader>
          <CardContent>
            {quarterlyRevenue.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No revenue data for {taxYear}</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={quarterlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" className="text-xs" />
                  <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} className="text-xs" />
                  <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                  <Legend />
                  <Bar dataKey="revenue" name="Revenue" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="taxCollected" name="Tax Collected" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Income Waterfall + Federal Bracket Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income Waterfall Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Income Waterfall</CardTitle>
          </CardHeader>
          <CardContent>
            {waterfallData.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No data available</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={waterfallData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" className="text-xs" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `$${(Math.abs(v) / 1000).toFixed(0)}k`} className="text-xs" />
                  <Tooltip
                    formatter={(value) => formatCurrency(Math.abs(Number(value)))}
                    labelFormatter={(label) => label}
                  />
                  <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                    {waterfallData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Federal Bracket Breakdown Table */}
        <Card>
          <CardHeader>
            <CardTitle>Federal Tax Bracket Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {!taxProjection || taxProjection.federalBracketBreakdown.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No bracket data available</p>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="grid grid-cols-4 gap-2 text-xs font-medium text-muted-foreground pb-2 border-b">
                  <div>Bracket</div>
                  <div className="text-right">Rate</div>
                  <div className="text-right">Income</div>
                  <div className="text-right">Tax</div>
                </div>
                {taxProjection.federalBracketBreakdown.map((b) => (
                  <div key={b.bracket} className="grid grid-cols-4 gap-2 text-sm py-1.5 border-b border-dashed last:border-0">
                    <div className="truncate text-xs" title={b.bracket}>{b.bracket}</div>
                    <div className="text-right font-mono">{formatPctShort(b.rate)}</div>
                    <div className="text-right font-mono">{formatCurrency(b.taxableInThisBracket)}</div>
                    <div className="text-right font-mono">{formatCurrency(b.taxForThisBracket)}</div>
                  </div>
                ))}
                <div className="grid grid-cols-4 gap-2 text-sm font-bold pt-2 border-t">
                  <div>Total</div>
                  <div className="text-right">{formatPct(taxProjection.federalEffectiveRate)}</div>
                  <div className="text-right">{formatCurrency(taxProjection.taxableIncome)}</div>
                  <div className="text-right">{formatCurrency(taxProjection.federalTax)}</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Deductions + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Deductions by Category */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Deductions by IRS Category</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/tax/deductions')}>
              View Details <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {deductionsByCategory.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Receipt className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No deductible items found for {taxYear}</p>
                <p className="text-xs mt-1">Mark expenses as tax deductible in the Billing dashboard</p>
              </div>
            ) : (
              <div className="space-y-3">
                {deductionsByCategory.slice(0, 10).map((cat) => {
                  const pct = summary?.totalDeductions
                    ? (cat.totalAmount / summary.totalDeductions) * 100
                    : 0;
                  return (
                    <div key={cat.taxCategory} className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium truncate">{cat.taxCategory}</span>
                          <span className="text-sm font-medium">{formatCurrency(cat.totalAmount)}</span>
                        </div>
                        <div className="h-2 rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full rounded-full bg-emerald-500"
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          />
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {cat.itemCount} items &middot; {pct.toFixed(1)}% of deductions
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/tax/deductions')}>
                <Receipt className="h-4 w-4 mr-2" />
                View All Deductions
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/tax/forms')}>
                <FileText className="h-4 w-4 mr-2" />
                Generate Tax Forms
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/tax/estimated')}>
                <PiggyBank className="h-4 w-4 mr-2" />
                Quarterly Payments
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/tax/projections')}>
                <Calculator className="h-4 w-4 mr-2" />
                Tax Projections
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/tax/audit-log')}>
                <BarChart3 className="h-4 w-4 mr-2" />
                Audit Log
              </Button>
            </CardContent>
          </Card>

          {/* Tax Reserve Tracker */}
          <Card>
            <CardHeader>
              <CardTitle>Tax Reserve Tracker</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Annual liability</span>
                  <span className="font-medium">{formatCurrency(totalTaxOwed)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Estimated paid</span>
                  <span className="font-medium text-emerald-600">{formatCurrency(estimatedPaid)}</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-emerald-500 transition-all"
                    style={{ width: `${totalTaxOwed > 0 ? Math.min((estimatedPaid / totalTaxOwed) * 100, 100) : 0}%` }}
                  />
                </div>
                <div className="border-t pt-2">
                  <div className="flex justify-between text-sm font-medium">
                    <span>Remaining</span>
                    <span className={remainingLiability > 0 ? 'text-amber-600' : 'text-emerald-600'}>
                      {formatCurrency(remainingLiability)}
                    </span>
                  </div>
                </div>
                {taxProjection && (
                  <div className="border-t pt-2 space-y-1">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Quarterly estimate</span>
                      <span>{formatCurrency(taxProjection.quarterlyEstimate)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Monthly set-aside</span>
                      <span>{formatCurrency(taxProjection.monthlySetAside)}</span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
